// ============================================================================
// Mesa de Servicio — pantalla «Grupos de WhatsApp» (#549).
// ============================================================================
// Una sola pantalla para la pregunta "¿de quién es este grupo y quién escribe
// en él?":
//   • lista de grupos detectados, con aviso destacado en los que NO tienen
//     compañía (esos NUNCA crean tickets);
//   • vinculación a la compañía, con la sugerencia que hace el backend a partir
//     del nombre del grupo;
//   • participantes con sus candidatos por teléfono (usuario de la plataforma o
//     contacto del CRM) para confirmar o corregir;
//   • quién es personal nuestro (sus mensajes no son solicitudes de cliente);
//   • la auditoría: quién vinculó qué y cuándo;
//   • las SOLICITUDES DETECTADAS (#550): cada ráfaga de mensajes con su
//     clasificación, su confianza y su ticket, y un selector para corregirla;
//   • la REVISIÓN (#728, WhatsappReviewPanel): lo que las bandejas dejaron
//     esperando a una persona y las alertas de tráfico real descartado.
//
// Mientras no exista el conector oficial (#548) la pantalla trae un panel de
// SIMULACIÓN que inyecta eventos de fixtures, para poder recorrer el flujo
// entero sin un número de WhatsApp real.
import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { getError } from "../../../utils/getError";
import { isWaBotNoticeInDoubtError } from "../../../utils/apiErrorCodes";
import { formatDate } from "../services/serviceDeskService";
import {
  getWhatsappStatus,
  connectorLabel,
  CONNECTOR_SOURCE_LABEL,
  listWhatsappGroups,
  getWhatsappGroup,
  linkGroupCompany,
  linkParticipant,
  createWhatsappGroup,
  simulateWhatsappEvents,
  correctWhatsappCase,
  updateGroupBot,
  groupBotHandoff,
  botStatusLabel,
  botHumanRequestNotice,
  auditActionLabel,
  formatConfidence,
  ACTION_META,
  CATEGORY_META,
  CASE_CATEGORIES,
  CASE_STATUS_LABELS,
  bestSuggestion,
  canCreateTickets,
  DECISION_META,
  LINK_STATE_META,
  type WaChannelStatus,
  type WaGroup,
  type WaGroupDetail,
  type WaParticipant,
  type WaSimulationResult,
  type WaCase,
  type WaCaseCategory,
} from "../services/whatsappGroupsService";
import { DeskLoadErrorPanel, useDeskLoadError } from "../components/DeskLoadError";
import WhatsappReviewPanel from "./WhatsappReviewPanel";

// Límite oficial del asunto de un grupo en la API de WhatsApp (#712).
const MAX_GROUP_SUBJECT = 128;

const TONE_CLASSES: Record<string, string> = {
  ok: "bg-emerald-100 text-emerald-700",
  warn: "bg-amber-100 text-amber-800",
  info: "bg-blue-100 text-blue-700",
  muted: "bg-gray-100 text-gray-600",
};

function Chip({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[tone] || TONE_CLASSES.muted}`}>
      {children}
    </span>
  );
}

export default function WhatsappGroupsSection() {
  const { allCompanies, fetchAllCompanies } = useCompaniesStore();
  const { error: loadError, capture, reset } = useDeskLoadError();

  const [status, setStatus] = useState<WaChannelStatus | null>(null);
  const [groups, setGroups] = useState<WaGroup[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<WaGroupDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [simulation, setSimulation] = useState<WaSimulationResult | null>(null);
  const [newSubject, setNewSubject] = useState("");
  // Marca del grupo que se va a crear. Para un operador acotado por marca es
  // OBLIGATORIA: un grupo sin marca se le escondería a él mismo en cuanto lo
  // creara (el alcance por marca falla cerrado).
  const [newBrand, setNewBrand] = useState("");
  // Compañía del grupo nuevo. Con el conector oficial es OBLIGATORIA: el grupo
  // se crea con el número de WhatsApp de esa compañía (#712).
  const [newCompanyId, setNewCompanyId] = useState("");

  const companyRequired = status?.connector?.credentialsScope === "company";

  const card = "bg-surface border-line";
  const text = "text-content";
  const muted = "text-content-muted";
  const input = "w-full rounded-lg border px-3 py-2 text-sm border-line bg-app text-content placeholder:text-content-faint";

  const loadGroups = useCallback(async () => {
    const data = await listWhatsappGroups();
    setGroups(data.groups);
    return data.groups;
  }, []);

  useEffect(() => {
    reset();
    Promise.all([getWhatsappStatus().then(setStatus), loadGroups()]).catch(capture);
    fetchAllCompanies().catch(() => {});
  }, [capture, reset, loadGroups, fetchAllCompanies]);

  // Con una sola marca legible no hay nada que elegir: se preselecciona (el
  // backend hace lo mismo si no se manda).
  useEffect(() => {
    const brands = status?.brands || [];
    if (status?.brandRequired && brands.length === 1) setNewBrand((b) => b || brands[0].id);
  }, [status]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    getWhatsappGroup(selectedId)
      .then(setDetail)
      .catch((err) => Swal.fire({ icon: "error", title: "No se pudo abrir el grupo", text: getError(err) }));
  }, [selectedId]);

  const refreshDetail = useCallback(async () => {
    if (!selectedId) return;
    setDetail(await getWhatsappGroup(selectedId));
    await loadGroups();
  }, [selectedId, loadGroups]);

  const unlinkedCount = useMemo(() => (groups || []).filter((g) => !canCreateTickets(g)).length, [groups]);

  const run = async (fn: () => Promise<void>, errorTitle: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      Swal.fire({ icon: "error", title: errorTitle, text: getError(err) });
    } finally {
      setBusy(false);
    }
  };

  const onLinkCompany = (groupId: string, companyId: string, brand?: string | null) =>
    run(async () => {
      await linkGroupCompany(groupId, companyId, brand);
      await refreshDetail();
      await loadGroups();
    }, "No se pudo vincular el grupo");

  const onLinkParticipant = (participant: WaParticipant, patch: Parameters<typeof linkParticipant>[2]) =>
    run(async () => {
      if (!selectedId) return;
      await linkParticipant(selectedId, participant.id, patch);
      await refreshDetail();
    }, "No se pudo actualizar el participante");

  const onCorrectCase = (kase: WaCase, category: WaCaseCategory) =>
    run(async () => {
      if (!selectedId || kase.category === category) return;
      await correctWhatsappCase(selectedId, kase.id, category);
      await refreshDetail();
    }, "No se pudo corregir la clasificación");

  // Bot del grupo (#556). Encenderlo la primera vez PUBLICA el aviso de
  // privacidad en el grupo: se pide confirmación antes.
  const onToggleBot = (group: WaGroup, enabled: boolean) =>
    run(async () => {
      if (enabled && !group.bot?.noticeSentAt) {
        const ok = await Swal.fire({
          icon: "question",
          title: "¿Encender el bot en este grupo?",
          text:
            "Se publicará en el grupo un aviso de privacidad para los clientes. El bot no habla hasta que " +
            "WhatsApp confirme que el aviso salió; si WhatsApp lo rechaza, el bot no se enciende.",
          showCancelButton: true,
          confirmButtonText: "Encender y avisar",
          cancelButtonText: "Cancelar",
        });
        if (!ok.isConfirmed) return;
      }
      let updated;
      try {
        updated = await updateGroupBot(group.id, { enabled });
      } catch (err) {
        if (!enabled || !isWaBotNoticeInDoubtError(err)) throw err;
        // El aviso anterior PUDO llegar al grupo (quedó en duda o se resolvió
        // en revisión sin confirmar que salió): no se republica solo. Quien
        // revisó el grupo dice si llegó o si hay que publicarlo otra vez.
        const choice = await Swal.fire({
          icon: "warning",
          title: "¿Llegó el aviso de privacidad al grupo?",
          text:
            "El aviso anterior pudo haber llegado al grupo. Revisa el grupo de WhatsApp: si el aviso está ahí, " +
            "confirma que llegó y el bot se enciende sin volver a publicarlo. Si no está, publícalo otra vez.",
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonText: "Sí llegó",
          denyButtonText: "No llegó: publicarlo otra vez",
          cancelButtonText: "Cancelar",
        });
        if (choice.isDismissed) return;
        updated = await updateGroupBot(
          group.id,
          choice.isConfirmed ? { enabled, noticeConfirmed: true } : { enabled, republishNotice: true },
        );
      }
      await refreshDetail();
      if (enabled && updated.notice && !updated.notice.confirmed) {
        Swal.fire({
          icon: "info",
          title: "Bot encendido, aviso en camino",
          text:
            "El aviso de privacidad está en la bandeja de salida y se reintenta solo. El bot no habla en el " +
            "grupo hasta que WhatsApp confirme que salió.",
        });
      }
    }, "No se pudo cambiar el bot del grupo");

  const onHandoff = (group: WaGroup, action: "take" | "release") =>
    run(async () => {
      const result = await groupBotHandoff(group.id, action);
      await refreshDetail();
      if (action === "take" && result.handoffNoticeSent === false && group.bot?.enabled) {
        Swal.fire({
          icon: "warning",
          title: "El bot quedó en pausa",
          text: "Pero el aviso «te atiende una persona» no se pudo publicar en el grupo.",
        });
      }
    }, "No se pudo hacer el traspaso");

  const onCreateGroup = () =>
    run(async () => {
      const created = await createWhatsappGroup({
        subject: newSubject,
        ...(newBrand ? { brand: newBrand } : {}),
        ...(newCompanyId ? { companyId: newCompanyId } : {}),
      });
      setNewSubject("");
      await loadGroups();
      setSelectedId(created.id);
      Swal.fire({
        icon: "success",
        title: "Grupo creado",
        // `text`, no `html`: el enlace lo devuelve el conector (con la API
        // oficial, Meta). Interpolarlo en HTML haría que cualquier `<` o `&`
        // se interpretara como marcado en vez de verse tal cual.
        // Con la API oficial el enlace llega DESPUÉS, por webhook (#712).
        text: created.inviteLink
          ? `Comparte este enlace con el cliente: ${created.inviteLink}`
          : "WhatsApp manda el enlace de invitación en unos segundos: aparecerá en la ficha del grupo.",
      });
    }, "No se pudo crear el grupo");

  // `reset` repone la cola de eventos del simulador desde el archivo de
  // ejemplo: sin él, el recorrido (descubrir → vincular → volver a simular)
  // sólo se podía hacer UNA vez, porque la cola se consume.
  // La marca viaja también aquí: los grupos que la simulación DESCUBRA nacen
  // con ella. Sin marca, un operador acotado veía «procesados: 3» y una lista
  // vacía, porque su propio alcance esconde los grupos sin marca.
  const onSimulate = (reset = false) =>
    run(async () => {
      const result = await simulateWhatsappEvents({ reset, brand: newBrand || undefined });
      setSimulation(result);
      await loadGroups();
      if (selectedId) await refreshDetail();
    }, "No se pudo simular");

  if (loadError) return <DeskLoadErrorPanel error={loadError} />;
  if (!groups) return <div className="p-8 text-center text-gray-400">Cargando…</div>;

  const selected = detail?.group || null;

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className={`text-xl font-semibold ${text}`}>Grupos de WhatsApp</h1>
        <p className={`mt-1 text-sm ${muted}`}>
          Vincula cada grupo con la compañía cliente y cada participante con su persona, para que los
          tickets se abran a nombre de quien corresponde.
        </p>
      </header>

      {/* --- Estado del canal + política de #547 --------------------------- */}
      {status && (
        <section className={`rounded-xl border p-4 ${card}`}>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={`text-sm font-semibold ${text}`}>Conector</h2>
            <Chip tone={status.connector.implemented ? "ok" : "warn"}>
              {connectorLabel(status.connector.id)}
            </Chip>
            {status.connectorResolution && (
              <Chip tone={status.connectorResolution.envIgnored ? "warn" : "muted"}>
                {CONNECTOR_SOURCE_LABEL[status.connectorResolution.source] || status.connectorResolution.source}
              </Chip>
            )}
            {!status.connector.implemented && status.connector.pendingIssue && (
              <Chip tone="warn">Pendiente: #{status.connector.pendingIssue}</Chip>
            )}
            <Chip tone="muted">Máx. {status.maxParticipants} participantes</Chip>
          </div>
          {/* Por qué aplica ESTE conector (#716 · D8): configuración guardada,
              variable del despliegue o valor por omisión. */}
          {status.connectorResolution && (
            <p className={`mt-2 text-xs ${muted}`} data-testid="wa-connector-reason">
              {status.connectorResolution.reason}
            </p>
          )}
          <p className={`mt-2 text-xs ${muted}`}>{status.policy}</p>
          {status.connector.detail && <p className={`mt-1 text-xs ${muted}`}>{status.connector.detail}</p>}
        </section>
      )}

      {/* El interruptor del canal apaga la ingesta de verdad: si está apagado,
          nada de lo que se escriba en los grupos entra, y crear o simular se
          rechaza. La pantalla lo dice en vez de parecer rota. */}
      {status && !status.settings.enabled && (
        <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <strong>El canal de WhatsApp está desactivado.</strong> No se procesa ningún mensaje de
          grupo ni se pueden crear grupos. Actívalo en Mesa de Servicio → Configuración → Canal de
          WhatsApp (grupos).
        </div>
      )}

      {/* --- Crear un grupo por API + simulación --------------------------- */}
      <section className={`rounded-xl border p-4 ${card}`}>
        <h2 className={`mb-2 text-sm font-semibold ${text}`}>Crear un grupo</h2>
        <p className={`mb-2 text-xs ${muted}`}>
          Los grupos los tiene que crear la mesa: WhatsApp no deja leer los que crearon las personas.
          Al crearlo se genera un enlace de invitación para mandar al cliente (con la API oficial llega
          unos segundos después). Nadie se agrega a mano: las personas entran por ese enlace, máximo{" "}
          {status?.maxParticipants ?? 8} por grupo.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            className={`${input} max-w-sm`}
            placeholder="Nombre del grupo (p. ej. Soporte ACME)"
            aria-label="Nombre del grupo"
            maxLength={MAX_GROUP_SUBJECT}
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
          />
          {(status?.brands || []).length > 0 && (
            <select
              className={`${input} max-w-xs`}
              aria-label="Marca del grupo nuevo"
              value={newBrand}
              disabled={busy}
              onChange={(e) => setNewBrand(e.target.value)}
            >
              <option value="">
                {status?.brandRequired ? "Elige la marca…" : "Sin marca"}
              </option>
              {(status?.brands || []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </select>
          )}
          <select
            className={`${input} max-w-xs`}
            aria-label="Compañía del grupo nuevo"
            value={newCompanyId}
            disabled={busy}
            onChange={(e) => setNewCompanyId(e.target.value)}
          >
            <option value="">{companyRequired ? "Elige la compañía…" : "Sin compañía (vincular después)"}</option>
            {allCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.business_name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            disabled={
              busy ||
              !newSubject.trim() ||
              newSubject.trim().length > MAX_GROUP_SUBJECT ||
              (Boolean(status?.brandRequired) && !newBrand) ||
              (companyRequired && !newCompanyId)
            }
            title={
              status?.brandRequired && !newBrand
                ? "Elige la marca: si el grupo nace sin marca, tu alcance ya no te dejaría verlo"
                : companyRequired && !newCompanyId
                  ? "Elige la compañía: el grupo se crea con su número de WhatsApp"
                  : undefined
            }
            onClick={onCreateGroup}
          >
            Crear grupo
          </button>
          {/* La simulación escribe grupos, participantes y auditoría DE VERDAD:
              es una herramienta de desarrollo y el despliegue la habilita a
              propósito (`SD_WA_SIMULATOR`). Apagada, el backend responde 403,
              así que la pantalla no ofrece el botón. */}
          {status?.connector.id === "simulator" && status?.simulatorEnabled && (
            <>
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium disabled:opacity-50"
                disabled={busy || (Boolean(status?.brandRequired) && !newBrand)}
                onClick={() => onSimulate(false)}
                title={
                  status?.brandRequired && !newBrand
                    ? "Elige la marca: los grupos que descubra la simulación nacerían sin marca y tu alcance no te dejaría verlos"
                    : "Procesa los eventos del archivo de fixtures como si llegaran de WhatsApp"
                }
              >
                Simular eventos entrantes
              </button>
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium disabled:opacity-50"
                disabled={busy || (Boolean(status?.brandRequired) && !newBrand)}
                onClick={() => onSimulate(true)}
                title="Recarga los eventos del archivo de ejemplo para repetir el recorrido completo"
              >
                Reiniciar y volver a simular
              </button>
            </>
          )}
        </div>

        {status?.brandRequired && (status?.brands || []).length === 0 ? (
          // El selector no se pinta (no hay ninguna marca que ofrecer) y el
          // botón se queda gris para siempre: sin este texto, la pantalla
          // parece rota en vez de explicar a quién hay que pedirle una marca.
          <p className={`mt-2 text-xs ${muted}`} role="status">
            Ninguna de tus marcas sigue en Mesa de Servicio → Configuración: pide que te asignen
            una marca vigente para poder crear grupos.
          </p>
        ) : (
          status?.brandRequired && (
            <p className={`mt-2 text-xs ${muted}`}>
              Tu usuario está acotado por marca: elige en qué marca nace el grupo, o no podrías
              volver a verlo.
            </p>
          )
        )}

        {simulation && (
          <div className={`mt-3 rounded-lg border p-3 text-xs ${card}`} role="status">
            <p className={text}>Eventos procesados: {simulation.processed}</p>
            <ul className="mt-1 space-y-1">
              {simulation.results.map((r, i) => (
                <li key={`${r.groupId}-${i}`} className="flex flex-wrap items-center gap-2">
                  <Chip tone={DECISION_META[r.decision]?.tone || "muted"}>
                    {DECISION_META[r.decision]?.label || r.decision}
                  </Chip>
                  {r.category && (
                    <Chip tone={CATEGORY_META[r.category]?.tone || "muted"}>
                      {CATEGORY_META[r.category]?.label || r.category}
                    </Chip>
                  )}
                  {r.action && (
                    <span className={text}>
                      {ACTION_META[r.action] || r.action}
                      {r.ticketReference ? ` · ${r.ticketReference}` : ""}
                    </span>
                  )}
                  <span className={muted}>{r.reason}</span>
                </li>
              ))}
            </ul>
            {simulation.outbox.length > 0 && (
              <div className={`mt-2 ${muted}`}>
                <p>El bot respondió en el grupo:</p>
                <ul className="mt-1 space-y-1">
                  {simulation.outbox.map((m) => (
                    <li key={m.providerMessageId} className="whitespace-pre-line">
                      “{m.text}”
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {simulation.pendingEvents === 0 && (
              <p className={`mt-2 ${muted}`}>
                Ya no quedan eventos de ejemplo por procesar: usa «Reiniciar y volver a simular»
                para repetir el recorrido.
              </p>
            )}
          </div>
        )}
      </section>

      {/* --- Revisión (#728): lo atorado en las bandejas + alertas de tráfico
          real descartado. Va antes de la lista: es lo que pide una persona YA. */}
      <WhatsappReviewPanel
        channelEnabled={Boolean(status?.settings.enabled)}
        companyName={(id) => {
          const c = allCompanies.find((x) => x.id === id);
          return c ? c.name || c.business_name || null : null;
        }}
      />

      {/* --- Aviso global -------------------------------------------------- */}
      {unlinkedCount > 0 && (
        <div role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <strong>{unlinkedCount}</strong>{" "}
          {unlinkedCount === 1 ? "grupo sin compañía vinculada" : "grupos sin compañía vinculada"}. Mientras
          no tengan compañía <strong>no se crean tickets</strong> con sus mensajes.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* --- Lista ------------------------------------------------------- */}
        <section className={`rounded-xl border ${card}`}>
          <h2 className={`border-b px-4 py-3 text-sm font-semibold ${text} border-line`}>
            Grupos detectados ({groups.length})
          </h2>
          {groups.length === 0 ? (
            <p className={`p-4 text-sm ${muted}`}>
              Todavía no hay grupos. Crea uno arriba y comparte el enlace de invitación con el cliente.
            </p>
          ) : (
            <ul>
              {groups.map((g) => (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(g.id)}
                    aria-current={selectedId === g.id}
                    className={`flex w-full flex-col gap-1 border-b px-4 py-3 text-left border-line ${selectedId === g.id ? "bg-primary-50/60" : ""}`}
                  >
                    <span className={`text-sm font-medium ${text}`}>{g.subject || g.providerGroupId}</span>
                    <span className="flex flex-wrap items-center gap-2">
                      {g.companyId ? (
                        <Chip tone="ok">{g.companyName || g.companyId}</Chip>
                      ) : (
                        <Chip tone="warn">Sin compañía · no crea tickets</Chip>
                      )}
                      <span className={`text-xs ${muted}`}>
                        {g.participantCount}/{g.maxParticipants} participantes
                      </span>
                      {g.lastMessageAt && (
                        <span className={`text-xs ${muted}`}>Último mensaje: {formatDate(g.lastMessageAt)}</span>
                      )}
                    </span>
                    {!g.companyId && g.companySuggestion && (
                      <span className={`text-xs ${muted}`}>
                        Sugerencia: {g.companySuggestion.companyName} ({g.companySuggestion.confidence === "high" ? "alta" : "media"})
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* --- Ficha del grupo --------------------------------------------- */}
        <section className={`rounded-xl border ${card}`}>
          {!selected ? (
            <p className={`p-4 text-sm ${muted}`}>Elige un grupo para vincularlo y revisar quién escribe en él.</p>
          ) : (
            <div className="space-y-4 p-4">
              <div>
                <h2 className={`text-sm font-semibold ${text}`}>{selected.subject || selected.providerGroupId}</h2>
                {selected.inviteLink && (
                  <p className={`mt-1 break-all text-xs ${muted}`}>Enlace de invitación: {selected.inviteLink}</p>
                )}
              </div>

              {selected.linkConflict?.active && (
                <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-900">
                  {selected.linkConflict.reason === "number_other_company" ? (
                    <>
                      Conflicto de compañía: este grupo vive en el número de WhatsApp de otra compañía, así que sus
                      mensajes se descartan y no crean tickets
                      {selected.linkConflict.droppedCount ? <> (descartados: {selected.linkConflict.droppedCount})</> : null}
                      . Vincúlalo a la compañía dueña del número.
                    </>
                  ) : (
                    <>
                      Conflicto de compañía: WhatsApp confirmó este grupo, pero su creación estaba vinculada a otra
                      compañía. No se cambió nada automáticamente; revisa y elige la compañía correcta.
                    </>
                  )}
                </div>
              )}

              {!selected.companyId && (
                <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  {selected.unlinkedWarning?.message ||
                    "Grupo sin compañía vinculada: no se crean tickets hasta que un agente lo vincule."}
                  {selected.unlinkedWarning?.count ? (
                    <> Mensajes bloqueados: {selected.unlinkedWarning.count}.</>
                  ) : null}
                </div>
              )}

              <label className="block">
                <span className={`mb-1 block text-xs font-medium ${muted}`}>Compañía cliente</span>
                <select
                  className={input}
                  aria-label="Compañía del grupo"
                  value={selected.companyId || ""}
                  disabled={busy}
                  onChange={(e) => onLinkCompany(selected.id, e.target.value)}
                >
                  <option value="">Sin vincular (no crea tickets)</option>
                  {allCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || c.business_name}
                    </option>
                  ))}
                </select>
              </label>

              {/* La marca decide QUIÉN ve este grupo: un operador acotado a una
                  marca no ve los grupos de las demás, ni los que aún no tienen
                  ninguna. */}
              <label className="block">
                <span className={`mb-1 block text-xs font-medium ${muted}`}>Marca</span>
                <select
                  className={input}
                  aria-label="Marca del grupo"
                  value={selected.brand || ""}
                  disabled={busy}
                  onChange={(e) => onLinkCompany(selected.id, selected.companyId || "", e.target.value)}
                >
                  {/* A quien está acotado por marca no se le ofrece «Sin marca»:
                      elegirla le escondería este mismo grupo al instante
                      (`visibleToScope` falla cerrado) y sólo un admin podría
                      devolvérselo. El servidor lo rechaza igualmente con 422;
                      esto es para que no llegue a intentarlo. */}
                  {!status?.brandRequired && (
                    <option value="">Sin marca (sólo lo ven los operadores sin alcance por marca)</option>
                  )}
                  {(status?.brands || []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </label>
              {selected.linkedByName && (
                <p className={`text-xs ${muted}`}>
                  Vinculado por {selected.linkedByName}
                  {selected.linkedAt ? ` · ${formatDate(selected.linkedAt)}` : ""}
                </p>
              )}

              {/* Bot del grupo (#556) */}
              <div className={`rounded-lg border p-3 border-line`}>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className={`text-sm font-semibold ${text}`}>Bot en este grupo</h3>
                  <Chip tone={selected.bot?.enabled && selected.bot?.noticeSentAt && !selected.bot?.humanActive ? "ok" : "muted"}>
                    {botStatusLabel(selected.bot)}
                  </Chip>
                </div>
                {botHumanRequestNotice(selected.bot) && (
                  <p
                    role="alert"
                    className={`mt-2 rounded-md border px-2 py-1 text-xs border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-transparent dark:text-amber-300`}
                  >
                    {botHumanRequestNotice(selected.bot)}
                  </p>
                )}
                <p className={`mt-1 text-xs ${muted}`}>
                  Sólo habla ante una solicitud de soporte, una mención o una respuesta a un mensaje suyo. Apagado,
                  los tickets se siguen creando en silencio.
                </p>
                {selected.bot && (
                  <p className={`mt-1 text-xs ${muted}`}>
                    Hoy: {selected.bot.usage.dayCount}/{selected.bot.limits.maxRepliesPerDay} mensajes · esta hora:{" "}
                    {selected.bot.usage.hourCount}/{selected.bot.limits.maxRepliesPerHour}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {status?.canOperateBot && (
                    <button
                      type="button"
                      className="rounded-lg border px-3 py-1 text-xs"
                      disabled={busy || (!selected.companyId && !selected.bot?.enabled)}
                      onClick={() => onToggleBot(selected, !selected.bot?.enabled)}
                    >
                      {selected.bot?.enabled ? "Apagar el bot" : "Encender el bot"}
                    </button>
                  )}
                  {!selected.bot?.humanActive ? (
                    <button
                      type="button"
                      className="rounded-lg border px-3 py-1 text-xs"
                      disabled={busy}
                      onClick={() => onHandoff(selected, "take")}
                    >
                      Tomar el grupo (te atiende una persona)
                    </button>
                  ) : (
                    status?.canOperateBot && (
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs"
                        disabled={busy}
                        onClick={() => onHandoff(selected, "release")}
                      >
                        Devolver el grupo al bot
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Participantes */}
              <div>
                <h3 className={`mb-2 text-sm font-semibold ${text}`}>
                  Participantes ({detail?.participants.length || 0})
                </h3>
                {(detail?.participants.length || 0) === 0 ? (
                  <p className={`text-sm ${muted}`}>Aún no ha escrito nadie en este grupo.</p>
                ) : (
                  <ul className="space-y-3">
                    {detail!.participants.map((p) => {
                      const suggestion = bestSuggestion(p);
                      const state = LINK_STATE_META[p.linkState];
                      return (
                        <li key={p.id} className={`rounded-lg border p-3 border-line`}>
                          <div className="flex flex-wrap items-center gap-2">
                            {/* El nombre DECLARADO no titula la ficha: lo escribió
                                el propio desconocido y no lo ha verificado nadie.
                                Va abajo, entrecomillado y como lo que es. */}
                            <span className={`text-sm font-medium ${text}`}>
                              {p.linkName || p.displayName || p.phoneMasked}
                            </span>
                            <Chip tone={state.tone}>{state.label}</Chip>
                            {p.isStaff && <Chip tone="muted">Personal interno</Chip>}
                            {p.left && <Chip tone="muted">Salió del grupo</Chip>}
                          </div>
                          <p className={`mt-1 text-xs ${muted}`}>
                            {p.phone} · {p.messageCount} mensaje(s)
                          </p>
                          {p.declaredName && (
                            <p className={`mt-1 text-xs ${muted}`}>
                              Dice llamarse “{p.declaredName}”
                              {p.declaredRole ? ` · ${p.declaredRole}` : ""} (sin verificar)
                            </p>
                          )}
                          {p.isStaff && (
                            <p className={`mt-1 text-xs ${muted}`}>
                              Sus mensajes no se tratan como solicitudes del cliente. Si te
                              equivocaste, desvincúlalo: así vuelve a cero y queda auditado.
                            </p>
                          )}

                          {p.linkState !== "confirmed" && suggestion && (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <span className={`text-xs ${muted}`}>
                                ¿Es <strong>{suggestion.name}</strong>
                                {suggestion.type === "crm_contact" ? " (contacto del CRM)" : ""}
                                {suggestion.companyMismatch ? " — ojo: es de otra compañía" : ""}?
                              </span>
                              {suggestion.companyUnresolved && !suggestion.companyMismatch && (
                                <span className={`text-xs ${muted}`} data-testid="wa-suggestion-unresolved">
                                  Su empresa en el CRM no está vinculada a ninguna compañía: confírmalo sólo si
                                  sabes que es de este cliente.
                                </span>
                              )}
                              {suggestion.phoneAmbiguous && (
                                <span className={`text-xs ${muted}`} data-testid="wa-suggestion-ambiguous">
                                  El número guardado no dice su país con certeza: comprueba que sea la misma persona.
                                </span>
                              )}
                              <button
                                type="button"
                                className="rounded border border-emerald-500 px-2 py-1 text-xs font-medium text-emerald-700 disabled:opacity-50"
                                // Sin compañía en el grupo no hay contra qué comprobar
                                // que esa persona sea del cliente correcto: el backend
                                // lo rechaza con 422 y el botón lo dice antes.
                                disabled={
                                  busy ||
                                  suggestion.companyMismatch ||
                                  (!selected.companyId &&
                                    (Boolean(suggestion.companyId) || Boolean(suggestion.companyUnresolved)) &&
                                    !suggestion.isStaff)
                                }
                                title={
                                  !selected.companyId &&
                                  (suggestion.companyId || suggestion.companyUnresolved) &&
                                  !suggestion.isStaff
                                    ? "Vincula primero el grupo a una compañía"
                                    : undefined
                                }
                                onClick={() =>
                                  onLinkParticipant(p, { linkType: suggestion.type, linkId: suggestion.id })
                                }
                              >
                                Confirmar
                              </button>
                              {!selected.companyId &&
                                (suggestion.companyId || suggestion.companyUnresolved) &&
                                !suggestion.isStaff && (
                                <span className={`text-xs ${muted}`}>
                                  Vincula primero el grupo a una compañía.
                                </span>
                              )}
                            </div>
                          )}

                          <div className="mt-2 flex flex-wrap gap-2">
                            {/* La marca de personal interno sólo va hacia arriba:
                                el backend rechaza quitarla (422). Quitarla de un
                                clic dejaba a un compañero marcado como cliente
                                para siempre —un vínculo confirmado ya no se
                                vuelve a detectar solo— y sus mensajes pasaban a
                                abrir tickets del cliente. La salida es
                                «Desvincular», que lo devuelve a cero y queda
                                auditado. */}
                            {!p.isStaff && (
                              <button
                                type="button"
                                className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50"
                                disabled={busy}
                                onClick={() => onLinkParticipant(p, { isStaff: true })}
                              >
                                Marcar como personal interno
                              </button>
                            )}
                            {/* …y por eso «Desvincular» tiene que existir también
                                cuando el participante sigue en `unknown`: a quien
                                se marcó a mano como personal interno se le puso
                                la bandera SIN vincularlo a nadie, así que se
                                quedaba en ese estado exacto y con el botón
                                escondido — sin una sola salida desde la pantalla
                                para deshacer el clic. */}
                            {(p.linkState !== "unknown" || p.isStaff) && (
                              <button
                                type="button"
                                className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50"
                                disabled={busy}
                                onClick={() => onLinkParticipant(p, { linkType: "unknown" })}
                              >
                                Desvincular
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Solicitudes detectadas (#550) */}
              <div>
                <h3 className={`mb-2 text-sm font-semibold ${text}`}>
                  Solicitudes detectadas ({detail?.cases?.length || 0})
                </h3>
                {(detail?.cases?.length || 0) === 0 ? (
                  <p className={`text-sm ${muted}`}>
                    Todavía no hay solicitudes en este grupo. La plática normal no aparece aquí.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {detail!.cases!.map((c) => (
                      <li key={c.id} className={`rounded-lg border p-2 text-xs ${card}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          {c.category && (
                            <Chip tone={CATEGORY_META[c.category]?.tone || "muted"}>
                              {CATEGORY_META[c.category]?.label || c.category}
                            </Chip>
                          )}
                          <span className={muted}>
                            Confianza {formatConfidence(c.confidence)}
                            {c.classificationSource === "staff" && c.correctedByName
                              ? ` · corregida por ${c.correctedByName}`
                              : c.classificationSource === "example"
                                ? " · por una corrección anterior"
                                : ""}
                          </span>
                          <span className={muted}>
                            · {c.status ? CASE_STATUS_LABELS[c.status] || c.status : "—"}
                            {c.ticketReference ? ` · ${c.ticketReference}` : ""}
                            {` · ${c.messageCount} ${c.messageCount === 1 ? "mensaje" : "mensajes"}`}
                          </span>
                        </div>
                        {c.firstText && (
                          <p className={`mt-1 ${text}`}>
                            {c.authorName ? <strong>{c.authorName}: </strong> : null}
                            {c.firstText}
                          </p>
                        )}
                        <label className={`mt-1 flex items-center gap-2 ${muted}`}>
                          Corregir clasificación
                          <select
                            aria-label={`Corregir clasificación del caso ${c.id}`}
                            className="rounded border border-gray-300 px-1 py-0.5 text-xs text-gray-900"
                            disabled={busy}
                            value={c.category || ""}
                            onChange={(e) => onCorrectCase(c, e.target.value as WaCaseCategory)}
                          >
                            {CASE_CATEGORIES.map((cat) => (
                              <option key={cat} value={cat}>
                                {CATEGORY_META[cat].label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Auditoría */}
              <div>
                <h3 className={`mb-2 text-sm font-semibold ${text}`}>Quién vinculó qué y cuándo</h3>
                {(detail?.audit.length || 0) === 0 ? (
                  <p className={`text-sm ${muted}`}>Sin movimientos todavía.</p>
                ) : (
                  <ul className="space-y-1">
                    {detail!.audit.map((a) => (
                      <li key={a.id} className={`text-xs ${muted}`}>
                        <span className={text}>{auditActionLabel(a.action)}</span>
                        {a.actorName ? ` · ${a.actorName}` : ""}
                        {a.createdAt ? ` · ${formatDate(a.createdAt)}` : ""}
                        {a.detail ? ` · ${a.detail}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
